import * as Yup from 'yup';
import { useMemo } from 'react';
import { Event } from '../../../@types/event';
import { sortLecturesForEdit } from './eventLectures';

export const EventSchema = (currentEvent?: Event) => {
  const NewEventSchema = Yup.object().shape({
    slug: Yup.string().min(2).required('Slug is required'),
    title: Yup.string().min(2).required('Title is required'),
    title_ar: Yup.string(),
    subtitle: Yup.string(),
    subtitle_ar: Yup.string(),
    description: Yup.string(),
    description_ar: Yup.string(),
    location: Yup.string(),
    location_ar: Yup.string(),
    date: Yup.string(),
    bannerImage: Yup.string(),
    ctaText: Yup.string(),
    ctaText_ar: Yup.string(),
    active: Yup.boolean().default(true),
    assignedUsers: Yup.array(),
    // Cohorts. All batches share the same lectures/quizzes; a batch only
    // separates who is in it, its dates/instructor, and the quiz analysis.
    batches: Yup.array().of(
      Yup.object().shape({
        _id: Yup.string(),
        name: Yup.string().min(1).required('Batch name is required'),
        name_ar: Yup.string(),
        startDate: Yup.string(),
        endDate: Yup.string(),
        capacity: Yup.number()
          .typeError('Capacity must be a number')
          .min(0)
          .nullable()
          .transform((v, o) => (o === '' ? null : v)),
        active: Yup.boolean(),
        students: Yup.array(),
      })
    ),
    // Mirrors the API (@IsInt @Min(0)): a decimal or a negative order is a
    // field error here, not a generic "Unable to edit Program" from the server.
    order: Yup.number()
      .typeError('Order must be a number')
      .integer('Order must be a whole number')
      .min(0, 'Order cannot be negative')
      .required('Order is required'),
    lectures: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().min(1).required('Lecture title is required'),
        title_ar: Yup.string(),
        info: Yup.string(),
        info_ar: Yup.string(),
        speaker: Yup.string(),
        speaker_ar: Yup.string(),
        thumbnail: Yup.string(),
        htmlUrl: Yup.string(),
        htmlUrl_ar: Yup.string(),
        labFile: Yup.string(),
        // No input on screen (the order is changed with the arrows); mirrors
        // the API's @IsInt only — no new minimum (R4-E2d). An error here is
        // listed in the form's top Alert, never a silent blocked Save.
        order: Yup.number()
          .typeError('Order must be a number')
          .integer('Order must be a whole number'),
        status: Yup.string().oneOf(['published', 'soon']),
        // Batch ids this lecture is hidden for (set per batch in the Batches
        // editor). Declared so it is retained through validation.
        excludedBatches: Yup.array(),
        // Optional embedded quiz — passed through untouched (the builder UI
        // enforces its own field rules; Yup.mixed keeps the nested shape intact).
        quiz: Yup.mixed().nullable(),
      })
    ),
  });

  const defaultValues: any = useMemo(
    () => ({
      slug: currentEvent?.slug || '',
      title: currentEvent?.title || '',
      title_ar: currentEvent?.title_ar || '',
      subtitle: currentEvent?.subtitle || '',
      subtitle_ar: currentEvent?.subtitle_ar || '',
      description: currentEvent?.description || '',
      description_ar: currentEvent?.description_ar || '',
      location: currentEvent?.location || '',
      location_ar: currentEvent?.location_ar || '',
      date: currentEvent?.date ? String(currentEvent.date).slice(0, 10) : '',
      bannerImage: currentEvent?.bannerImage || '',
      ctaText: currentEvent?.ctaText || '',
      ctaText_ar: currentEvent?.ctaText_ar || '',
      active: currentEvent?.active ?? true,
      assignedUsers: currentEvent?.assignedUsers || [],
      // Normalise dates to yyyy-mm-dd for the date inputs, and keep _id so the
      // backend updates the existing batch instead of minting a new one (which
      // would orphan that batch's stamped quiz history).
      batches: (currentEvent?.batches || []).map((b: any) => ({
        _id: b?._id ? String(b._id) : undefined,
        name: b?.name || '',
        name_ar: b?.name_ar || '',
        startDate: b?.startDate ? String(b.startDate).slice(0, 10) : '',
        endDate: b?.endDate ? String(b.endDate).slice(0, 10) : '',
        capacity: b?.capacity ?? '',
        active: b?.active ?? true,
        students: (b?.students || []).map((s: any) => String(s)),
      })),
      order: currentEvent?.order ?? 0,
      // Normalise quiz.active on the edit path. RHFSwitch renders
      // `checked={field.value}`, so an undefined value would show every
      // pre-existing quiz as switched OFF (and warn about an uncontrolled ->
      // controlled input on first click).
      // Normalised for EVERY lecture, not only those that already have a quiz:
      // LectureQuizBuilder renders for all of them, so a lecture with no quiz
      // object would otherwise leave `quiz.active` undefined — MUI would paint
      // the switch OFF (uncontrolled) while the builder computed active
      // (`!== false`) and showed no "Disabled" chip, contradicting itself.
      // In the order the program page shows them (by `order`, ties by their
      // place), so "Lecture N" here is position N on the website (R4-E3).
      lectures: sortLecturesForEdit(currentEvent?.lectures || []).map((l: any) => ({
        ...l,
        // Batch ids this lecture is hidden for — normalised to strings so the
        // per-batch checkbox comparison matches (Mongo returns ObjectIds).
        excludedBatches: (l?.excludedBatches ?? []).map((b: any) => String(b)),
        quiz: {
          ...(l?.quiz ?? {}),
          active: l?.quiz?.active !== false,
          // Explicit boolean so RHFSwitch stays controlled; defaults CLOSED,
          // unlike `active`, because revealing answers must be opted into.
          showAnswers: l?.quiz?.showAnswers === true,
          // Normalised to strings so the Autocomplete's value comparison
          // matches (Mongo returns ObjectIds, the picker compares by String).
          excludedBatches: (l?.quiz?.excludedBatches ?? []).map((b: any) => String(b)),
        },
      })),
    }),
    [currentEvent]
  );

  return { NewEventSchema, defaultValues };
};
export { default as LectureAssignmentBuilder } from './LectureAssignmentBuilder';
